export const DEFAULT_OPEN_TIME = '09:00';
export const DEFAULT_CLOSE_TIME = '22:00';
export const ALL_DAY_OPEN_TIME = '00:00';
export const ALL_DAY_CLOSE_TIME = '23:59';

// ── Domain types ──────────────────────────────────────────────────────────────

export interface ScheduleGroup {
  localId: string;
  serverId?: string;
  mask: number;
  openTime: string;
  closeTime: string;
  /** «Сотрудники должны приходить за N мин» для этой категории дней; null/undefined = дефолт зоны. */
  staffArrivalMinutes?: number | null;
}

export interface ScheduleExceptionDraft {
  localId: string;
  serverId?: string;
  date: string;
  openTime: string | null;
  closeTime: string | null;
  periodLocalId: string | 'off';
}

export interface WorkingHoursEditorState {
  groups: ScheduleGroup[];
  exceptions: ScheduleExceptionDraft[];
  forceWeekendCard: boolean;
}

export interface ScheduleDraftState {
  groups: ScheduleGroup[];
  exceptions: ScheduleExceptionDraft[];
}

// ── ID factories ──────────────────────────────────────────────────────────────

let groupLocalCounter = 0;
let exceptionCounter = 0;

export function nextGroupLocalId(): string { return `grp-${++groupLocalCounter}`; }
export function nextExceptionLocalId(): string { return `exc-${++exceptionCounter}`; }

// ── Constants & shared types ──────────────────────────────────────────────────

export const ALL_DAYS_MASK = 0b1111111;
export type CardTarget = string | 'off';
export type ExceptionDraftTarget = CardTarget | 'new';

// ── Predicates ────────────────────────────────────────────────────────────────

export function isAllDayRange(openTime: string, closeTime: string): boolean {
  return openTime === closeTime || (openTime === ALL_DAY_OPEN_TIME && closeTime === ALL_DAY_CLOSE_TIME);
}

export function isSingleFullWeekAllDayGroup(groups: ScheduleGroup[]): boolean {
  return groups.length === 1
    && groups[0].mask === ALL_DAYS_MASK
    && isAllDayRange(groups[0].openTime, groups[0].closeTime);
}

/** Closed = no active intervals and all exceptions are "off". */
export function isClosedState(
  groups: ScheduleGroup[],
  exceptions: ScheduleExceptionDraft[],
): boolean {
  return groups.every(g => g.mask === 0)
    && exceptions.every(ex => ex.periodLocalId === 'off');
}

/** Explicit 24×7 = exactly one all-day full-week group, no exceptions. */
export function isExplicit24x7State(
  groups: ScheduleGroup[],
  exceptions: ScheduleExceptionDraft[],
): boolean {
  return isSingleFullWeekAllDayGroup(groups) && exceptions.length === 0;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Days not owned by any group (the "weekend card" days). */
export function getOffMask(groups: ScheduleGroup[]): number {
  return ALL_DAYS_MASK & ~groups.reduce((acc, g) => acc | g.mask, 0);
}

/** Active groups (mask≠0), used by ScheduleDisplay for the summary view. */
export function getActiveGroups(groups: ScheduleGroup[]): ScheduleGroup[] {
  return groups.filter(g => g.mask !== 0);
}

/**
 * Groups that represent real user intent, excluding persistence-only sentinels.
 *
 * A mask=0 group with a serverId is a sentinel (carries the API row ID so the
 * save-diff can delete the right row). User-created groups never have a serverId,
 * so `!g.serverId` is the reliable discriminator for "added this session".
 */
export function getEditableGroups(
  groups: ScheduleGroup[],
  exceptions: ScheduleExceptionDraft[],
): ScheduleGroup[] {
  return groups.filter(
    g => g.mask !== 0
      || !g.serverId
      || exceptions.some(ex => ex.periodLocalId === g.localId),
  );
}

export function dayOwner(groups: ScheduleGroup[], day: number): CardTarget {
  return groups.find(g => (g.mask & (1 << day)) !== 0)?.localId ?? 'off';
}

/** Times to use for a new interval; avoids propagating all-day times to a sibling. */
function inheritTimes(openTime?: string | null, closeTime?: string | null) {
  const o = openTime ?? DEFAULT_OPEN_TIME;
  const c = closeTime ?? DEFAULT_CLOSE_TIME;
  return isAllDayRange(o, c)
    ? { openTime: DEFAULT_OPEN_TIME, closeTime: DEFAULT_CLOSE_TIME }
    : { openTime: o, closeTime: c };
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function addInterval(state: WorkingHoursEditorState): WorkingHoursEditorState {
  const base = getEditableGroups(state.groups, state.exceptions);
  const newGroup = { localId: nextGroupLocalId(), mask: 0, ...inheritTimes(base[0]?.openTime, base[0]?.closeTime) };
  return {
    ...state,
    groups: [...base, newGroup],
    // From closed state (no editable groups), force cards mode so the empty card is visible.
    forceWeekendCard: base.length === 0 ? true : state.forceWeekendCard,
  };
}

export function addWeekendCard(state: WorkingHoursEditorState): WorkingHoursEditorState {
  return { ...state, forceWeekendCard: true };
}

export function assignDay(
  state: WorkingHoursEditorState,
  day: number,
  target: CardTarget,
): WorkingHoursEditorState {
  const bit = 1 << day;
  const nextGroups = state.groups.map(g => {
    const hasDay = (g.mask & bit) !== 0;
    if (target === g.localId && !hasDay) return { ...g, mask: g.mask | bit };
    if (target !== g.localId && hasDay) return { ...g, mask: g.mask & ~bit };
    return g;
  });
  return {
    ...state,
    groups: nextGroups,
    // Pull-up: clear forceWeekendCard when state just became explicit 24×7.
    forceWeekendCard: state.forceWeekendCard && !isExplicit24x7State(nextGroups, state.exceptions),
  };
}

export function updateGroup(
  state: WorkingHoursEditorState,
  id: string,
  patch: Partial<ScheduleGroup>,
): WorkingHoursEditorState {
  return {
    ...state,
    groups: state.groups.map(g => g.localId === id ? { ...g, ...patch } : g),
  };
}

export function toggleAllDay(
  state: WorkingHoursEditorState,
  groupId: string,
): WorkingHoursEditorState {
  const group = state.groups.find(g => g.localId === groupId);
  if (!group) return state;
  return updateGroup(state, groupId, isAllDayRange(group.openTime, group.closeTime)
    ? { openTime: DEFAULT_OPEN_TIME, closeTime: DEFAULT_CLOSE_TIME }
    : { openTime: ALL_DAY_OPEN_TIME, closeTime: ALL_DAY_CLOSE_TIME });
}

export function removeException(
  state: WorkingHoursEditorState,
  localId: string,
): WorkingHoursEditorState {
  return { ...state, exceptions: state.exceptions.filter(ex => ex.localId !== localId) };
}

export function assignException(
  state: WorkingHoursEditorState,
  exceptionId: string,
  target: CardTarget,
): WorkingHoursEditorState {
  return {
    ...state,
    exceptions: state.exceptions.map(exc => {
      if (exc.localId !== exceptionId) return exc;
      if (target === 'off') return { ...exc, periodLocalId: 'off', openTime: null, closeTime: null };
      const group = state.groups.find(g => g.localId === target);
      if (!group) return exc;
      return { ...exc, periodLocalId: target, openTime: group.openTime, closeTime: group.closeTime };
    }),
  };
}

export function dropExceptionToNewInterval(
  state: WorkingHoursEditorState,
  exceptionId: string,
): WorkingHoursEditorState {
  const exception = state.exceptions.find(ex => ex.localId === exceptionId);
  if (!exception) return state;
  const newId = nextGroupLocalId();
  const times = inheritTimes(
    exception.openTime ?? state.groups[0]?.openTime,
    exception.closeTime ?? state.groups[0]?.closeTime,
  );
  return {
    ...state,
    groups: [...state.groups, { localId: newId, mask: 0, ...times }],
    exceptions: state.exceptions.map(ex =>
      ex.localId !== exceptionId ? ex :
        { ...ex, periodLocalId: newId, openTime: times.openTime, closeTime: times.closeTime },
    ),
  };
}

export function deleteCard(
  state: WorkingHoursEditorState,
  target: CardTarget,
): WorkingHoursEditorState {
  if (target === 'off') {
    const offMask = getOffMask(state.groups);
    const primary = state.groups.find(g => g.mask !== 0);
    const groups = offMask !== 0 && primary
      ? state.groups.map(g => g.localId === primary.localId ? { ...g, mask: g.mask | offMask } : g)
      : state.groups;
    return {
      ...state,
      groups,
      exceptions: state.exceptions.filter(ex => ex.periodLocalId !== 'off'),
      forceWeekendCard: false,
    };
  }
  return {
    ...state,
    groups: state.groups.filter(g => g.localId !== target),
    exceptions: state.exceptions.filter(ex => ex.periodLocalId !== target),
  };
}

export function resetToDefaultSchedule(state: WorkingHoursEditorState): WorkingHoursEditorState {
  return {
    ...state,
    groups: [{ localId: nextGroupLocalId(), mask: ALL_DAYS_MASK, openTime: DEFAULT_OPEN_TIME, closeTime: DEFAULT_CLOSE_TIME }],
    exceptions: [],
    forceWeekendCard: false,
  };
}

/** Constructs a new exception draft with a fresh localId. */
function mkException(
  date: string,
  periodLocalId: ScheduleExceptionDraft['periodLocalId'],
  openTime: string | null,
  closeTime: string | null,
): ScheduleExceptionDraft {
  return { localId: nextExceptionLocalId(), date, periodLocalId, openTime, closeTime };
}

// ── Exception popover helpers ─────────────────────────────────────────────────

/** Groups selectable when adding an exception (excludes 24×7 all-day groups). */
export function getExceptionIntervalTargets(groups: ScheduleGroup[]): ScheduleGroup[] {
  return groups.filter(g => g.mask !== 0 && !(g.mask === ALL_DAYS_MASK && isAllDayRange(g.openTime, g.closeTime)));
}

export function canAddWeekendException(groups: ScheduleGroup[]): boolean {
  return groups.some(g => g.mask !== 0) || groups.length === 0;
}

export function normalizeExceptionTarget(
  target: ExceptionDraftTarget | null,
  groups: ScheduleGroup[],
): ExceptionDraftTarget | null {
  if (target === null || target === 'new') return target;
  if (target === 'off') return canAddWeekendException(groups) ? target : null;
  return getExceptionIntervalTargets(groups).some(g => g.localId === target) ? target : null;
}

export function addDraftException(
  state: ScheduleDraftState,
  date: string,
  target: ExceptionDraftTarget,
): { state: ScheduleDraftState; error?: string } {
  if (state.exceptions.some(ex => ex.date === date)) {
    return { state, error: 'Исключение на эту дату уже добавлено.' };
  }

  if (target === 'off') {
    if (!canAddWeekendException(state.groups)) return { state };
    const groups = state.groups.length === 0
      ? [{ localId: nextGroupLocalId(), mask: ALL_DAYS_MASK, openTime: ALL_DAY_OPEN_TIME, closeTime: ALL_DAY_CLOSE_TIME }]
      : state.groups;
    return { state: { groups, exceptions: [...state.exceptions, mkException(date, 'off', null, null)] } };
  }

  if (target === 'new') {
    const base = getEditableGroups(state.groups, state.exceptions);
    const newId = nextGroupLocalId();
    return {
      state: {
        groups: [...base, { localId: newId, mask: 0, openTime: DEFAULT_OPEN_TIME, closeTime: DEFAULT_CLOSE_TIME }],
        exceptions: [...state.exceptions, mkException(date, newId, DEFAULT_OPEN_TIME, DEFAULT_CLOSE_TIME)],
      },
    };
  }

  const intervalTargets = getExceptionIntervalTargets(state.groups);
  const group = intervalTargets.find(g => g.localId === target) ?? intervalTargets[0];
  if (!group) return { state };
  return {
    state: {
      ...state,
      exceptions: [...state.exceptions, mkException(date, group.localId, group.openTime, group.closeTime)],
    },
  };
}
